import React, { useState, useEffect, useRef } from 'react';
import { Navigate } from 'react-router-dom';
import Header from '../../compos/Header/Header';
import UserAdminPanel from '../UserAdminPanel/UserAdminPanel';
import AuthorityAdminPanel from '../AuthorityAdminPanel/AuthorityAdminPanel';
import AnalyticsPanel from '../AnalyticsPanel/AnalyticsPanel';
import OrganisationMapping from '../OrganisationMapping/OrganisationMapping';
import ManagedAccounts from '../ManagedAccounts/ManagedAccounts';
import QRCodes from '../QRCodes/QRCodes';
import UploadPost from '../UploadPost/UploadPost';

import './AdminPanel.css';
import { useUser } from '../../UserContext';
import {
	LayoutDashboard,
	Users,
	Shield,
	PlusSquare,
	ArrowRight,
	Monitor,
	BarChart3,
	Construction,
	MapPinned,
	UserPlus,
	QrCode,
} from 'lucide-react';
// testing git
const AdminPanel = () => {
	const { isAdmin, isLoggedIn, isAuthLoading } = useUser();
	const [activeComponent, setActiveComponent] = useState<string>(() => sessionStorage.getItem('adminActiveTab') ?? '');
	// Desktop-only guard — mirrors the lg (1024px) breakpoint
	const [isDesktop, setIsDesktop] = useState<boolean>(() => window.innerWidth >= 1024);
	useEffect(() => {
		const handleResize = () => setIsDesktop(window.innerWidth >= 1024);
		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, []);

	// Ref holding the cancel-redirect function registered by UploadPost
	const navigateAwayFromUploadRef = useRef<(() => void) | null>(null);

	const handleSetActiveComponent = (next: string) => {
		// If leaving UploadPost, fire the cancel callback so it won't redirect
		if (activeComponent === 'UploadPost' && navigateAwayFromUploadRef.current) {
			navigateAwayFromUploadRef.current();
			navigateAwayFromUploadRef.current = null;
		}
		sessionStorage.setItem('adminActiveTab', next);
		setActiveComponent(next);
	};

	const renderActiveComponent = () => {
		switch (activeComponent) {
			case '':
				return (
					<div className='px-4 pb-5'>
						<div className='mb-10'>
							<h1 className='text-4xl font-extrabold text-gray-900 mb-2'>Admin Panel</h1>
							<p className='text-xl text-gray-600'>
								Welcome back. Monitor, manage, and moderate the BReporter ecosystem from this central hub.
							</p>
						</div>

						<div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8'>
							{/* Normal Users Module */}
							<div
								onClick={() => handleSetActiveComponent('UserAdminPanel')}
								className='group bg-white p-8 rounded-2xl shadow-sm border border-gray-100 hover:shadow-xl hover:border-blue-200 transition-all duration-300 cursor-pointer'
							>
								<div className='relative flex items-center justify-center mb-6 min-h-[3.5rem]'>
									<div className='absolute left-0 w-14 h-14 bg-blue-50 text-blue-600 rounded-xl flex items-center justify-center group-hover:bg-blue-600 group-hover:text-white transition-colors duration-300'>
										<Users className='w-8 h-8' />
									</div>
									<h3 className='text-2xl font-bold text-blue-600 m-0'>Users</h3>
								</div>
								<p className='text-gray-600 leading-relaxed mb-4'>
									Search for user profiles, monitor activity, and manage access. You can block or delete accounts to
									maintain community standards.
								</p>
								<div className='flex items-center text-blue-600 font-semibold'>
									Manage Users{' '}
									<ArrowRight className='w-5 h-5 ml-1 group-hover:translate-x-2 transition-transform duration-300' />
								</div>
							</div>

							{/* Authorities Module */}
							<div
								onClick={() => handleSetActiveComponent('AuthorityAdminPanel')}
								className='group bg-white p-8 rounded-2xl shadow-sm border border-gray-100 hover:shadow-xl hover:border-blue-200 transition-all duration-300 cursor-pointer'
							>
								<div className='relative flex items-center justify-center mb-6 min-h-[3.5rem]'>
									<div className='absolute left-0 w-14 h-14 bg-indigo-50 text-indigo-600 rounded-xl flex items-center justify-center group-hover:bg-indigo-600 group-hover:text-white transition-colors duration-300'>
										<Shield className='w-8 h-8' />
									</div>
									<h3 className='text-2xl font-bold text-indigo-600 m-0'>Authorities</h3>
								</div>
								<p className='text-gray-600 leading-relaxed mb-4'>
									Verify and onboard news organizations, police departments, and other official entities. Maintain the
									integrity of official reporting.
								</p>
								<div className='flex items-center text-indigo-600 font-semibold'>
									Manage Authorities{' '}
									<ArrowRight className='w-5 h-5 ml-1 group-hover:translate-x-2 transition-transform duration-300' />
								</div>
							</div>

							{/* Organisation Mapping Module */}
							<div
								onClick={() => handleSetActiveComponent('OrganisationMapping')}
								className='group bg-white p-8 rounded-2xl shadow-sm border border-gray-100 hover:shadow-xl hover:border-blue-200 transition-all duration-300 cursor-pointer'
							>
								<div className='relative flex items-center justify-center mb-6 min-h-[3.5rem]'>
									<div className='absolute left-0 w-14 h-14 bg-teal-50 text-teal-600 rounded-xl flex items-center justify-center group-hover:bg-teal-600 group-hover:text-white transition-colors duration-300'>
										<MapPinned className='w-8 h-8' />
									</div>
									<h3 className='text-2xl font-bold text-teal-600 m-0'>Organizations</h3>
								</div>
								<p className='text-gray-600 leading-relaxed mb-4'>
									Draw the campus perimeter for each organisation. The app checks a reporter's location against it
									before letting them file a report to that organisation.
								</p>
								<div className='flex items-center text-teal-600 font-semibold'>
									Map Boundaries{' '}
									<ArrowRight className='w-5 h-5 ml-1 group-hover:translate-x-2 transition-transform duration-300' />
								</div>
							</div>

							{/* Managed Accounts Module */}
							<div
								onClick={() => handleSetActiveComponent('ManagedAccounts')}
								className='group bg-white p-8 rounded-2xl shadow-sm border border-gray-100 hover:shadow-xl hover:border-blue-200 transition-all duration-300 cursor-pointer'
							>
								<div className='relative flex items-center justify-center mb-6 min-h-[3.5rem]'>
									<div className='absolute left-0 w-14 h-14 bg-sky-50 text-sky-600 rounded-xl flex items-center justify-center group-hover:bg-sky-600 group-hover:text-white transition-colors duration-300'>
										<UserPlus className='w-8 h-8' />
									</div>
									<h3 className='text-2xl font-bold text-sky-600 m-0'>Accounts</h3>
								</div>
								<p className='text-gray-600 leading-relaxed mb-4'>
									Create reserved-number accounts for positions like caretakers and reset their passwords.
								</p>
								<div className='flex items-center text-sky-600 font-semibold'>
									Manage Accounts{' '}
									<ArrowRight className='w-5 h-5 ml-1 group-hover:translate-x-2 transition-transform duration-300' />
								</div>
							</div>

							{/* QR Codes Module */}
							<div
								onClick={() => handleSetActiveComponent('QRCodes')}
								className='group bg-white p-8 rounded-2xl shadow-sm border border-gray-100 hover:shadow-xl hover:border-blue-200 transition-all duration-300 cursor-pointer'
							>
								<div className='relative flex items-center justify-center mb-6 min-h-[3.5rem]'>
									<div className='absolute left-0 w-14 h-14 bg-amber-50 text-amber-600 rounded-xl flex items-center justify-center group-hover:bg-amber-600 group-hover:text-white transition-colors duration-300'>
										<QrCode className='w-8 h-8' />
									</div>
									<h3 className='text-2xl font-bold text-amber-600 m-0'>QR Codes</h3>
								</div>
								<p className='text-gray-600 leading-relaxed mb-4'>
									Create, print and share the QR codes placed around campus, and keep track of where each one went.
								</p>
								<div className='flex items-center text-amber-600 font-semibold'>
									Manage QR Codes{' '}
									<ArrowRight className='w-5 h-5 ml-1 group-hover:translate-x-2 transition-transform duration-300' />
								</div>
							</div>

							{/* Analytics Module */}
							<div
								onClick={() => handleSetActiveComponent('AnalyticsPanel')}
								className='group bg-white p-8 rounded-2xl shadow-sm border border-gray-100 hover:shadow-xl hover:border-blue-200 transition-all duration-300 cursor-pointer'
							>
								<div className='relative flex items-center justify-center mb-6 min-h-[3.5rem]'>
									<div className='absolute left-0 w-14 h-14 bg-violet-50 text-violet-600 rounded-xl flex items-center justify-center group-hover:bg-violet-600 group-hover:text-white transition-colors duration-300'>
										<BarChart3 className='w-8 h-8' />
									</div>
									<h3 className='text-2xl font-bold text-violet-600 m-0'>Analytics</h3>
								</div>
								<p className='text-gray-600 leading-relaxed mb-4'>
									Track growth, engagement and reporting throughput — signups, active users, installs,
									website traffic and how quickly authorities close reports.
								</p>
								<div className='flex items-center text-violet-600 font-semibold'>
									View Analytics{' '}
									<ArrowRight className='w-5 h-5 ml-1 group-hover:translate-x-2 transition-transform duration-300' />
								</div>
							</div>

							{/* Create Post Module */}
							<div
								onClick={() => handleSetActiveComponent('UploadPost')}
								className='group bg-white p-8 rounded-2xl shadow-sm border border-gray-100 hover:shadow-xl hover:border-blue-200 transition-all duration-300 cursor-pointer'
							>
								<div className='relative flex items-center justify-center mb-6 min-h-[3.5rem]'>
									<div className='absolute left-0 w-14 h-14 bg-emerald-50 text-emerald-600 rounded-xl flex items-center justify-center group-hover:bg-emerald-600 group-hover:text-white transition-colors duration-300'>
										<PlusSquare className='w-8 h-8' />
									</div>
									<h3 className='text-2xl font-bold text-emerald-600 m-0'>Create Post</h3>
								</div>
								<p className='text-gray-600 leading-relaxed mb-4'>
									Publish official news, alerts, or updates directly to the platform feed. Use administrative tools to
									highlight critical information.
								</p>
								<div className='flex items-center text-emerald-600 font-semibold'>
									Publish News{' '}
									<ArrowRight className='w-5 h-5 ml-1 group-hover:translate-x-2 transition-transform duration-300' />
								</div>
							</div>
						</div>

						<div className='mt-8 py-3 px-8 bg-gradient-to-r from-blue-600 to-indigo-700 rounded-2xl text-white'>
							<div className='max-w-3xl mx-auto text-center'>
								<h2 className='text-lg font-bold mb-0.5 font-Montserrat'>! System Notice !</h2>
								<p className='text-blue-100 text-sm leading-tight'>
									The Admin Panel provides elevated privileges. All actions are logged. Ensure you verify data before
									performing high-impact actions like account deletion or post suppression.
								</p>
							</div>
						</div>
					</div>
				);
			case 'UserAdminPanel':
				return <UserAdminPanel />;
			case 'AuthorityAdminPanel':
				return <AuthorityAdminPanel />;
			case 'AnalyticsPanel':
				return <AnalyticsPanel />;
			case 'ManagedAccounts':
				return <ManagedAccounts />;
			case 'QRCodes':
				return <QRCodes />;
			case 'OrganisationMapping':
				return <OrganisationMapping />;
			case 'UploadPost':
				return (
					<UploadPost
						onNavigateAway={(cancel) => {
							navigateAwayFromUploadRef.current = cancel;
						}}
					/>
				);
			// Anything else — a tab we haven't built, or a stale name left in
			// sessionStorage by an older build. Say so instead of rendering a
			// blank content area.
			default:
				return (
					<div className='max-w-4xl mx-auto px-4 pb-5'>
						<h1 className='text-3xl font-extrabold text-blue-900 mb-8'>{activeComponent}</h1>
						<div className='bg-white rounded-2xl shadow-sm border border-gray-100 p-10 text-center'>
							<div className='w-14 h-14 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-5'>
								<Construction className='w-7 h-7 text-gray-400' />
							</div>
							<h2 className='text-xl font-bold text-gray-900 mb-2'>Not implemented yet</h2>
							<p className='text-gray-500 leading-relaxed mb-6'>
								This section doesn't exist yet. Pick one of the tabs on the left.
							</p>
							<button
								onClick={() => handleSetActiveComponent('')}
								className='inline-flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2 font-bold text-white transition-colors hover:bg-blue-700'
							>
								Back to Dashboard
							</button>
						</div>
					</div>
				);
		}
	};

	// if (user == null) {
	//   return <Navigate to="/login" />;
	// }

	// Auth guard — redirect to login if not authenticated or not an admin
	if (!isAuthLoading && (!isLoggedIn || !isAdmin)) {
		return <Navigate to='/login' replace />;
	}

	// While auth is still being determined, render nothing (avoids flash)
	if (isAuthLoading) {
		return null;
	}

	// Block access on mobile / small screens (< 1024px)
	if (!isDesktop) {
		return (
			<>
				<Header />
				<div className='min-h-screen bg-gray-50 flex flex-col items-center justify-center px-6 text-center'>
					<div className='bg-white rounded-2xl shadow-sm border border-gray-100 p-10 max-w-sm w-full'>
						<div className='w-16 h-16 bg-blue-50 rounded-2xl flex items-center justify-center mx-auto mb-6'>
							<Monitor className='w-8 h-8 text-blue-600' />
						</div>
						<h1 className='text-2xl font-extrabold text-gray-900 mb-3'>Desktop Only</h1>
						<p className='text-gray-500 leading-relaxed'>
							The Admin Panel is only accessible from a desktop or laptop computer. Please switch to a PC to continue.
						</p>
					</div>
				</div>
			</>
		);
	}

	return (
		<>
			<Header />
			<div className='admin-panel-bg d-flex flex-column justify-content-between'>
				{/* Navbar */}

				{/* <div className="vertical-nav">
        <ul className="nav nav-underline flex-column">
          <li className="nav-item">
            <a className={`nav-link vertical-nav-bar ${activeComponent === '' ? 'active' : ''}`} aria-current="page"  onClick={() => setActiveComponent('')}>Home</a>
          </li>
          <li className="nav-item">
            <a className={`nav-link  vertical-nav-bar ${activeComponent === 'UserAdminPanel' ? 'active' : ''}`} href="#" onClick={() => setActiveComponent('UserAdminPanel')}>User</a>
          </li>
          <li className="nav-item">
            <a className={`nav-link vertical-nav-bar ${activeComponent === 'AuthorityAdminPanel' ? 'active' : ''}`} href="#" onClick={() => setActiveComponent('AuthorityAdminPanel')}>Authority</a>
          </li>
          <li className="nav-item">
            <a className={`nav-link  vertical-nav-bar ${activeComponent === 'UploadPost' ? 'active' : ''}`} href="#" onClick={() => setActiveComponent('UploadPost')}>Upload Post</a>
          </li>
          
        </ul>
      </div> */}
				<div className='flex flex-row h-full min-h-screen pt-[var(--header-height,80px)]'>
					{/* Sidebar */}
					<nav className='fixed left-0 top-[var(--header-height,80px)] w-60 h-[calc(100vh-var(--header-height,80px))] bg-white border-r border-gray-200 lg:block hidden z-40 overflow-y-auto shadow-sm'>
						<div className='py-3 space-y-1'>
							{/* Section 1: Navigation */}
							<div className='px-3'>
								<button
									onClick={() => handleSetActiveComponent('')}
									className={`w-full flex items-center px-3.5 py-2.5 rounded-xl transition-all duration-200 group ${activeComponent === ''
										? 'bg-blue-50 text-blue-700 font-bold shadow-sm'
										: 'text-gray-600 hover:bg-gray-50 hover:text-gray-900 font-semibold'
										}`}
								>
									<LayoutDashboard
										className={`w-5 h-5 mr-3 shrink-0 ${activeComponent === '' ? 'text-blue-600' : 'text-gray-400 group-hover:text-gray-600'}`}
									/>
									<span className='text-base leading-none'>Dashboard</span>
								</button>
							</div>

							<div className='px-4 py-2'>
								<hr className='m-0 p-0 border-t-2 border-gray-300' />
							</div>

							{/* Section 2: User Management */}
							<div className='px-3 space-y-1'>
								<button
									onClick={() => handleSetActiveComponent('UserAdminPanel')}
									className={`w-full flex items-center px-3.5 py-2.5 rounded-xl transition-all duration-200 group ${activeComponent === 'UserAdminPanel'
										? 'bg-blue-50 text-blue-700 font-bold shadow-sm'
										: 'text-gray-600 hover:bg-gray-50 hover:text-gray-900 font-semibold'
										}`}
								>
									<Users
										className={`w-5 h-5 mr-3 shrink-0 ${activeComponent === 'UserAdminPanel' ? 'text-blue-600' : 'text-gray-400 group-hover:text-gray-600'}`}
									/>
									<span className='text-base leading-none'>Users</span>
								</button>

								<button
									onClick={() => handleSetActiveComponent('AuthorityAdminPanel')}
									className={`w-full flex items-center px-3.5 py-2.5 rounded-xl transition-all duration-200 group ${activeComponent === 'AuthorityAdminPanel'
										? 'bg-blue-50 text-blue-700 font-bold shadow-sm'
										: 'text-gray-600 hover:bg-gray-50 hover:text-gray-900 font-semibold'
										}`}
								>
									<Shield
										className={`w-5 h-5 mr-3 shrink-0 ${activeComponent === 'AuthorityAdminPanel' ? 'text-blue-600' : 'text-gray-400 group-hover:text-gray-600'}`}
									/>
									<span className='text-base leading-none'>Authorities</span>
								</button>

								<button
									onClick={() => handleSetActiveComponent('OrganisationMapping')}
									className={`w-full flex items-center px-3.5 py-2.5 rounded-xl transition-all duration-200 group ${activeComponent === 'OrganisationMapping'
										? 'bg-blue-50 text-blue-700 font-bold shadow-sm'
										: 'text-gray-600 hover:bg-gray-50 hover:text-gray-900 font-semibold'
										}`}
								>
									<MapPinned
										className={`w-5 h-5 mr-3 shrink-0 ${activeComponent === 'OrganisationMapping' ? 'text-blue-600' : 'text-gray-400 group-hover:text-gray-600'}`}
									/>
									<span className='text-base leading-none'>Organizations</span>
								</button>

								<button
									onClick={() => handleSetActiveComponent('QRCodes')}
									className={`w-full flex items-center px-3.5 py-2.5 rounded-xl transition-all duration-200 group ${activeComponent === 'QRCodes'
										? 'bg-blue-50 text-blue-700 font-bold shadow-sm'
										: 'text-gray-600 hover:bg-gray-50 hover:text-gray-900 font-semibold'
										}`}
								>
									<QrCode
										className={`w-5 h-5 mr-3 shrink-0 ${activeComponent === 'QRCodes' ? 'text-blue-600' : 'text-gray-400 group-hover:text-gray-600'}`}
									/>
									<span className='text-base leading-none'>QR Codes</span>
								</button>
							</div>

							<div className='px-4 py-2'>
								<hr className='m-0 p-0 border-t-2 border-gray-300' />
							</div>

							{/* Section 3: Reserved-number accounts */}
							<div className='px-3'>
								<button
									onClick={() => handleSetActiveComponent('ManagedAccounts')}
									className={`w-full flex items-center px-3.5 py-2.5 rounded-xl transition-all duration-200 group ${activeComponent === 'ManagedAccounts'
										? 'bg-blue-50 text-blue-700 font-bold shadow-sm'
										: 'text-gray-600 hover:bg-gray-50 hover:text-gray-900 font-semibold'
										}`}
								>
									<UserPlus
										className={`w-5 h-5 mr-3 shrink-0 ${activeComponent === 'ManagedAccounts' ? 'text-blue-600' : 'text-gray-400 group-hover:text-gray-600'}`}
									/>
									<span className='text-base leading-none'>Accounts</span>
								</button>
							</div>

							<div className='px-4 py-2'>
								<hr className='m-0 p-0 border-t-2 border-gray-300' />
							</div>

							{/* Section 4: Insights */}
							<div className='px-3'>
								<button
									onClick={() => handleSetActiveComponent('AnalyticsPanel')}
									className={`w-full flex items-center px-3.5 py-2.5 rounded-xl transition-all duration-200 group ${activeComponent === 'AnalyticsPanel'
										? 'bg-blue-50 text-blue-700 font-bold shadow-sm'
										: 'text-gray-600 hover:bg-gray-50 hover:text-gray-900 font-semibold'
										}`}
								>
									<BarChart3
										className={`w-5 h-5 mr-3 shrink-0 ${activeComponent === 'AnalyticsPanel' ? 'text-blue-600' : 'text-gray-400 group-hover:text-gray-600'}`}
									/>
									<span className='text-base leading-none'>Analytics</span>
								</button>
							</div>

							<div className='px-4 py-2'>
								<hr className='m-0 p-0 border-t-2 border-gray-300' />
							</div>

							{/* Section 5: Content */}
							<div className='px-3'>
								<button
									onClick={() => handleSetActiveComponent('UploadPost')}
									className={`w-full flex items-center px-3.5 py-2.5 rounded-xl transition-all duration-200 group ${activeComponent === 'UploadPost'
										? 'bg-blue-50 text-blue-700 font-bold shadow-sm'
										: 'text-gray-600 hover:bg-gray-50 hover:text-gray-900 font-semibold'
										}`}
								>
									<PlusSquare
										className={`w-5 h-5 mr-3 shrink-0 ${activeComponent === 'UploadPost' ? 'text-blue-600' : 'text-gray-400 group-hover:text-gray-600'}`}
									/>
									<span className='text-base leading-none'>Create Post</span>
								</button>
							</div>
						</div>
					</nav>

					{/* Content Area */}
					<div className='content-area flex-1 lg:ml-60 bg-gray-50 min-h-[calc(100vh-var(--header-height,80px))] p-8'>
						<div className='max-w-7xl mx-auto'>{renderActiveComponent()}</div>
					</div>
				</div>
			</div>
		</>
	);
};

export default AdminPanel;
